import { create } from 'zustand'
import type { NotificationLevel } from '@/types'

export type CurrentView = 'server' | 'home' | 'dm' | 'server-settings' | 'user-settings'
export type HomeTab = 'your-servers' | 'explore'
export type ServerSettingsTab = 'overview' | 'channels' | 'roles' | 'members' | 'invites' | 'safety' | 'billing' | 'emoji' | 'audit-log' | 'webhooks'
export type FriendsTab = 'all' | 'online' | 'pending' | 'blocked'
export type UserSettingsTab = 'account' | 'privacy' | 'appearance' | 'voice-video' | 'notifications' | 'keybinds' | 'subscription'

interface ContextMenuState {
  type: 'server' | 'channel' | 'message' | null
  targetId: string | null
  x: number
  y: number
}

interface AppState {
  // View routing
  currentView: CurrentView

  // Navigation
  activeServerId: string | null
  activeChannelId: string | null
  activeThreadMessageId: string | null

  // Home
  homeTab: HomeTab

  // Server settings
  serverSettingsTab: ServerSettingsTab

  // User settings
  userSettingsTab: UserSettingsTab

  // Friends / DMs
  friendsTab: FriendsTab
  activeDmChannelId: string | null

  // UI state
  threadPanelOpen: boolean
  threadListOpen: boolean
  commandPaletteOpen: boolean
  inboxOpen: boolean
  memberSidebarOpen: boolean
  searchOpen: boolean
  pinnedOpen: boolean
  emojiPickerOpen: boolean
  scheduledEventsOpen: boolean
  keyboardShortcutsOpen: boolean
  streamerMode: boolean
  compactMode: boolean

  // NSFW acknowledgements
  acknowledgedNsfwChannels: string[]

  // Message editing / replying
  editingMessageId: string | null
  replyingToMessageId: string | null

  // Voice
  voiceChannelId: string | null
  isMuted: boolean
  isDeafened: boolean

  // Profile modal
  profileModalUserId: string | null

  // Notifications / muting
  mutedServerIds: string[]
  mutedChannelIds: string[]
  channelNotificationLevels: Record<string, NotificationLevel>
  desktopNotifications: boolean
  soundEnabled: boolean
  dmNotificationLevel: NotificationLevel

  // Context menu
  contextMenu: ContextMenuState

  // Modals
  createServerModalOpen: boolean
  joinServerModalOpen: boolean
  addFriendModalOpen: boolean
  serverHeaderDropdownOpen: boolean
  deleteMessageId: string | null
  imagePreviewUrl: string | null

  // Welcome screen
  welcomeScreenServerId: string | null

  // Error/offline state
  connectionStatus: 'connected' | 'connecting' | 'reconnecting' | 'offline'

  // Actions — navigation
  setActiveServer: (serverId: string) => void
  setActiveChannel: (channelId: string) => void
  navigateHome: () => void
  navigateToDms: () => void
  navigateToServerSettings: (serverId: string) => void
  navigateToServer: (serverId: string, channelId?: string) => void
  navigateToUserSettings: () => void

  // Actions — tabs
  setHomeTab: (tab: HomeTab) => void
  setServerSettingsTab: (tab: ServerSettingsTab) => void
  setFriendsTab: (tab: FriendsTab) => void
  setActiveDmChannel: (channelId: string) => void
  setUserSettingsTab: (tab: UserSettingsTab) => void

  // Actions — panels
  toggleMemberSidebar: () => void
  toggleSearch: () => void
  togglePinned: () => void
  setEmojiPickerOpen: (open: boolean) => void
  toggleScheduledEvents: () => void
  setScheduledEventsOpen: (open: boolean) => void
  toggleThreadList: () => void
  toggleKeyboardShortcuts: () => void
  setKeyboardShortcutsOpen: (open: boolean) => void
  acknowledgeNsfwChannel: (channelId: string) => void
  toggleStreamerMode: () => void
  setCompactMode: (compact: boolean) => void

  // Actions — threads
  openThread: (messageId: string) => void
  closeThread: () => void

  // Actions — command palette
  toggleCommandPalette: () => void
  setCommandPaletteOpen: (open: boolean) => void

  // Actions — inbox
  toggleInbox: () => void
  setInboxOpen: (open: boolean) => void

  // Actions — voice
  joinVoiceChannel: (channelId: string) => void
  leaveVoiceChannel: () => void
  toggleMute: () => void
  toggleDeafen: () => void

  // Actions — profile
  openProfileModal: (userId: string) => void
  closeProfileModal: () => void

  // Actions — notifications
  toggleMuteServer: (serverId: string) => void
  toggleMuteChannel: (channelId: string) => void
  setChannelNotificationLevel: (channelId: string, level: NotificationLevel) => void
  setDesktopNotifications: (enabled: boolean) => void
  setSoundEnabled: (enabled: boolean) => void
  setDmNotificationLevel: (level: NotificationLevel) => void

  // Actions — context menu
  openContextMenu: (type: 'server' | 'channel' | 'message', targetId: string, x: number, y: number) => void
  closeContextMenu: () => void

  // Actions — modals
  openCreateServerModal: () => void
  closeCreateServerModal: () => void
  openJoinServerModal: () => void
  closeJoinServerModal: () => void
  openAddFriendModal: () => void
  closeAddFriendModal: () => void
  setServerHeaderDropdownOpen: (open: boolean) => void

  // Actions — message editing
  setEditingMessageId: (id: string | null) => void
  setReplyingToMessageId: (id: string | null) => void
  setDeleteMessageId: (id: string | null) => void

  // Actions — welcome screen
  setWelcomeScreenServerId: (id: string | null) => void

  // Actions — image preview
  setImagePreviewUrl: (url: string | null) => void

  // Actions — connection
  setConnectionStatus: (status: 'connected' | 'connecting' | 'reconnecting' | 'offline') => void
}

export const useAppStore = create<AppState>((set) => ({
  currentView: 'server',

  activeServerId: 's1',
  activeChannelId: 'ch3',
  activeThreadMessageId: null,

  homeTab: 'your-servers',
  serverSettingsTab: 'overview',
  userSettingsTab: 'account',
  friendsTab: 'all',
  activeDmChannelId: null,

  threadPanelOpen: false,
  threadListOpen: false,
  commandPaletteOpen: false,
  inboxOpen: false,
  memberSidebarOpen: false,
  searchOpen: false,
  pinnedOpen: false,
  emojiPickerOpen: false,
  scheduledEventsOpen: false,
  keyboardShortcutsOpen: false,
  streamerMode: false,
  compactMode: false,
  acknowledgedNsfwChannels: [],

  editingMessageId: null,
  replyingToMessageId: null,

  voiceChannelId: 'ch6',
  isMuted: false,
  isDeafened: false,

  profileModalUserId: null,

  mutedServerIds: [],
  mutedChannelIds: [],
  channelNotificationLevels: {},
  desktopNotifications: true,
  soundEnabled: true,
  dmNotificationLevel: 'all',

  contextMenu: { type: null, targetId: null, x: 0, y: 0 },

  createServerModalOpen: false,
  joinServerModalOpen: false,
  addFriendModalOpen: false,
  serverHeaderDropdownOpen: false,
  deleteMessageId: null,
  imagePreviewUrl: null,

  welcomeScreenServerId: null,

  connectionStatus: 'connected',

  setActiveServer: (serverId) =>
    set({
      activeServerId: serverId,
      activeChannelId: null,
      activeThreadMessageId: null,
      threadPanelOpen: false,
      inboxOpen: false,
    }),

  setActiveChannel: (channelId) =>
    set({
      activeChannelId: channelId,
      activeThreadMessageId: null,
      threadPanelOpen: false,
    }),

  navigateHome: () =>
    set({
      currentView: 'home',
      activeServerId: null,
      activeChannelId: null,
      threadPanelOpen: false,
    }),

  navigateToDms: () =>
    set({
      currentView: 'dm',
      activeServerId: null,
      activeChannelId: null,
      threadPanelOpen: false,
    }),

  navigateToServerSettings: (serverId) =>
    set({
      currentView: 'server-settings',
      activeServerId: serverId,
      serverSettingsTab: 'overview',
    }),

  navigateToServer: (serverId, channelId) =>
    set({
      currentView: 'server',
      activeServerId: serverId,
      activeChannelId: channelId || null,
      threadPanelOpen: false,
    }),

  navigateToUserSettings: () =>
    set({
      currentView: 'user-settings',
      userSettingsTab: 'account',
    }),

  setHomeTab: (tab) => set({ homeTab: tab }),
  setServerSettingsTab: (tab) => set({ serverSettingsTab: tab }),
  setFriendsTab: (tab) => set({ friendsTab: tab }),
  setActiveDmChannel: (channelId) => set({ activeDmChannelId: channelId }),
  setUserSettingsTab: (tab) => set({ userSettingsTab: tab }),

  toggleMemberSidebar: () => set((state) => ({ memberSidebarOpen: !state.memberSidebarOpen })),
  toggleSearch: () => set((state) => ({ searchOpen: !state.searchOpen })),
  togglePinned: () => set((state) => ({ pinnedOpen: !state.pinnedOpen })),
  setEmojiPickerOpen: (open) => set({ emojiPickerOpen: open }),
  toggleScheduledEvents: () => set((state) => ({ scheduledEventsOpen: !state.scheduledEventsOpen })),
  setScheduledEventsOpen: (open) => set({ scheduledEventsOpen: open }),
  toggleThreadList: () => set((state) => ({ threadListOpen: !state.threadListOpen })),
  toggleKeyboardShortcuts: () => set((state) => ({ keyboardShortcutsOpen: !state.keyboardShortcutsOpen })),
  setKeyboardShortcutsOpen: (open) => set({ keyboardShortcutsOpen: open }),
  acknowledgeNsfwChannel: (channelId) => set((state) => ({ acknowledgedNsfwChannels: [...state.acknowledgedNsfwChannels, channelId] })),
  toggleStreamerMode: () => set((state) => ({ streamerMode: !state.streamerMode })),
  setCompactMode: (compact) => set({ compactMode: compact }),

  openThread: (messageId) =>
    set({
      activeThreadMessageId: messageId,
      threadPanelOpen: true,
    }),

  closeThread: () =>
    set({
      activeThreadMessageId: null,
      threadPanelOpen: false,
    }),

  toggleCommandPalette: () =>
    set((state) => ({ commandPaletteOpen: !state.commandPaletteOpen })),

  setCommandPaletteOpen: (open) =>
    set({ commandPaletteOpen: open }),

  toggleInbox: () =>
    set((state) => ({ inboxOpen: !state.inboxOpen })),

  setInboxOpen: (open) =>
    set({ inboxOpen: open }),

  joinVoiceChannel: (channelId) =>
    set({ voiceChannelId: channelId }),

  leaveVoiceChannel: () =>
    set({ voiceChannelId: null }),

  toggleMute: () =>
    set((state) => ({ isMuted: !state.isMuted })),

  toggleDeafen: () =>
    set((state) => ({
      isDeafened: !state.isDeafened,
      isMuted: !state.isDeafened ? true : state.isMuted,
    })),

  openProfileModal: (userId) =>
    set({ profileModalUserId: userId }),

  closeProfileModal: () =>
    set({ profileModalUserId: null }),

  toggleMuteServer: (serverId) =>
    set((state) => ({
      mutedServerIds: state.mutedServerIds.includes(serverId)
        ? state.mutedServerIds.filter((id) => id !== serverId)
        : [...state.mutedServerIds, serverId],
    })),

  toggleMuteChannel: (channelId) =>
    set((state) => ({
      mutedChannelIds: state.mutedChannelIds.includes(channelId)
        ? state.mutedChannelIds.filter((id) => id !== channelId)
        : [...state.mutedChannelIds, channelId],
    })),

  setChannelNotificationLevel: (channelId, level) =>
    set((state) => ({
      channelNotificationLevels: { ...state.channelNotificationLevels, [channelId]: level },
    })),

  setDesktopNotifications: (enabled) => set({ desktopNotifications: enabled }),
  setSoundEnabled: (enabled) => set({ soundEnabled: enabled }),
  setDmNotificationLevel: (level) => set({ dmNotificationLevel: level }),

  openContextMenu: (type, targetId, x, y) =>
    set({ contextMenu: { type, targetId, x, y } }),

  closeContextMenu: () =>
    set({ contextMenu: { type: null, targetId: null, x: 0, y: 0 } }),

  openCreateServerModal: () => set({ createServerModalOpen: true }),
  closeCreateServerModal: () => set({ createServerModalOpen: false }),
  openJoinServerModal: () => set({ joinServerModalOpen: true }),
  closeJoinServerModal: () => set({ joinServerModalOpen: false }),
  openAddFriendModal: () => set({ addFriendModalOpen: true }),
  closeAddFriendModal: () => set({ addFriendModalOpen: false }),
  setServerHeaderDropdownOpen: (open) => set({ serverHeaderDropdownOpen: open }),

  setWelcomeScreenServerId: (id) => set({ welcomeScreenServerId: id }),

  setEditingMessageId: (id) => set({ editingMessageId: id }),
  setReplyingToMessageId: (id) => set({ replyingToMessageId: id }),
  setDeleteMessageId: (id) => set({ deleteMessageId: id }),

  setImagePreviewUrl: (url) => set({ imagePreviewUrl: url }),

  setConnectionStatus: (status) => set({ connectionStatus: status }),
}))
